import { validateUINodeSchema } from './schema-validation';
import { applyClassNames } from './dom/classnames';
import { applyAttributes, applyStyles } from './dom/attributes';
import { applyEventProp, applyEvents } from './dom/events';
import { applyRefBinding, cleanupAllRefBindings, cleanupRefBinding } from './dom/ref-bindings';
import { createDOMRangeManager } from './dom/ranges';
import { getTheme } from '../theme/theme_context';
import { resolveScopedTheme, stripInternalRuntimeProps, THEME_SCOPE_PROP } from './internal-props';
import { isClassProp, isEventProp, isManagedClassProp, isRefProp, isSemanticTagProp, isStyleProp, } from './dom/prop-flags';
import { createNode, mapTypeToTag } from './dom/node-factory';
const ELEMENT_NODE = 1;
const TEXT_NODE = 3;
export function createDOMPatchApplier(container, options = {}) {
    const document = options.document ?? window.document;
    const debug = options.debug ?? false;
    let mountContainer = options.preserveExistingContent
        ? document.createElement('div')
        : container;
    let initialMountCommitted = false;
    const nodeMap = new Map();
    const nodeTypes = new Map();
    const eventHandlers = new Map();
    const childMap = new Map();
    const parentMap = new Map();
    const nodeProps = new Map();
    const nodeThemes = new Map();
    const nodeComposedSchemaTypes = new Map();
    const nodeDataKeys = new Map();
    const refBindings = new Map();
    const inputStates = new Map();
    const pendingVideoReloads = new Set();
    const attributeContext = {
        document,
        nodeDataKeys,
        inputStates,
    };
    const rangeManager = createDOMRangeManager({
        nodeMap,
        nodeTypes,
        eventHandlers,
        childMap,
        parentMap,
        nodeProps,
        nodeThemes,
        nodeDataKeys,
        refBindings,
        inputStates,
    });
    let rootId = null;
    let lastCommitId = 0;
    const globalPatchHost = globalThis;
    const debugLog = debug
        ? (globalPatchHost.__smoothiePatchLog ??
            (globalPatchHost.__smoothiePatchLog = []))
        : null;
    const apply = (patches, meta) => {
        if (meta) {
            if (meta.lane !== 'dom') {
                throw new Error(`DOM patch applier cannot apply ${meta.lane} commit ${meta.commitId}`);
            }
            const expectedCommitId = lastCommitId + 1;
            if (meta.commitId !== expectedCommitId) {
                throw new Error(`DOM patch commit sequence mismatch: expected ${expectedCommitId}, received ${meta.commitId}`);
            }
        }
        for (const patch of patches) {
            if (debug) {
                globalThis.console?.info('[patch]', patch);
            }
            if (debugLog) {
                debugLog.push(patch);
            }
            switch (patch.op) {
                case 'create':
                    handleCreate(patch.node);
                    break;
                case 'remove':
                    handleRemove(patch.id);
                    break;
                case 'replace':
                    handleReplace(patch.id, patch.node);
                    break;
                case 'set-prop':
                    handleSetProp(patch.id, patch.name, patch.value);
                    break;
                case 'remove-prop':
                    handleRemoveProp(patch.id, patch.name);
                    break;
                case 'insert-child':
                    handleInsertChild(patch.id, patch.childId, patch.index);
                    break;
                case 'remove-child':
                    handleRemoveChild(patch.id, patch.childId);
                    break;
                case 'move-child':
                    handleMoveChild(patch.id, patch.childId, patch.index);
                    break;
                case 'set-text':
                    handleSetText(patch.id, patch.value);
                    break;
            }
        }
        flushPendingVideoReloads();
        if (!initialMountCommitted) {
            initialMountCommitted = true;
            if (options.preserveExistingContent) {
                container.replaceChildren(...mountContainer.childNodes);
                mountContainer = container;
            }
        }
        if (meta) {
            lastCommitId = meta.commitId;
        }
    };
    const handleCreate = (node) => {
        const created = createNode(node, document);
        nodeMap.set(node.id, created);
        nodeTypes.set(node.id, node.type);
        if (node.props) {
            nodeProps.set(node.id, { ...node.props });
            nodeThemes.set(node.id, resolveScopedTheme(node.props));
        }
        else {
            nodeProps.set(node.id, {});
            nodeThemes.set(node.id, getTheme());
        }
        if (node._runtimeComposedSchemaTypes?.length) {
            nodeComposedSchemaTypes.set(node.id, [...node._runtimeComposedSchemaTypes]);
        }
        else {
            nodeComposedSchemaTypes.delete(node.id);
        }
        if (node.type !== 'TextNode' && node.type !== 'Fragment') {
            applyAllProps(created, node.id, node.type);
        }
        if (node.text !== undefined) {
            setNodeText(created, node.text);
        }
        if (!rootId) {
            rootId = node.id;
            mountContainer.appendChild(created);
        }
    };
    const handleRemove = (id) => {
        if (!nodeMap.has(id)) {
            return;
        }
        rangeManager.detachRenderedRange(id);
        rangeManager.cleanupSubtree(id);
    };
    const handleReplace = (id, node) => {
        const target = nodeMap.get(id);
        const created = createNode(node, document);
        nodeMap.set(node.id, created);
        nodeTypes.set(node.id, node.type);
        if (node.props) {
            nodeProps.set(node.id, { ...node.props });
            nodeThemes.set(node.id, resolveScopedTheme(node.props));
        }
        else {
            nodeProps.set(node.id, {});
            nodeThemes.set(node.id, getTheme());
        }
        if (node._runtimeComposedSchemaTypes?.length) {
            nodeComposedSchemaTypes.set(node.id, [...node._runtimeComposedSchemaTypes]);
        }
        else {
            nodeComposedSchemaTypes.delete(node.id);
        }
        if (node.type !== 'TextNode' && node.type !== 'Fragment') {
            applyAllProps(created, node.id, node.type);
        }
        if (node.text !== undefined) {
            setNodeText(created, node.text);
        }
        if (target) {
            const parentId = parentMap.get(id);
            const parentContainer = (parentId ? rangeManager.getContainerForParent(parentId) : null) ?? target.parentNode;
            const nextSibling = rangeManager.getNodeAfterRange(id);
            rangeManager.detachRenderedRange(id);
            rangeManager.cleanupSubtree(id);
            if (parentId && parentContainer) {
                const siblings = childMap.get(parentId) ?? [];
                const index = siblings.indexOf(id);
                if (index >= 0) {
                    siblings[index] = node.id;
                    childMap.set(parentId, siblings);
                    parentMap.delete(id);
                    parentMap.set(node.id, parentId);
                }
            }
            if (parentContainer) {
                parentContainer.insertBefore(created, nextSibling);
                if (rootId === id) {
                    rootId = node.id;
                }
            }
            else if (rootId === id) {
                rootId = node.id;
                mountContainer.appendChild(created);
            }
        }
        else if (!rootId) {
            rootId = node.id;
            mountContainer.appendChild(created);
        }
    };
    const handleSetProp = (id, name, value) => {
        const node = nodeMap.get(id);
        if (!node || node.nodeType !== ELEMENT_NODE) {
            return;
        }
        const props = nodeProps.get(id) ?? {};
        props[name] = value;
        nodeProps.set(id, props);
        if (name === THEME_SCOPE_PROP) {
            nodeThemes.set(id, resolveScopedTheme(props));
        }
        const type = nodeTypes.get(id);
        if (isSemanticTagProp(type, name)) {
            const recreated = recreateElementForSemanticTagChange(id, type);
            if (recreated) {
                return;
            }
        }
        applyPropPatch(node, id, type, name);
        reloadParentVideoForSourceUpdate(id, name);
    };
    const handleRemoveProp = (id, name) => {
        const node = nodeMap.get(id);
        if (!node || node.nodeType !== ELEMENT_NODE) {
            return;
        }
        const props = nodeProps.get(id) ?? {};
        delete props[name];
        nodeProps.set(id, props);
        if (name === THEME_SCOPE_PROP) {
            nodeThemes.set(id, resolveScopedTheme(props));
        }
        if (name === 'indeterminate' && node.tagName === 'INPUT') {
            node.indeterminate = false;
        }
        const type = nodeTypes.get(id);
        if (isSemanticTagProp(type, name)) {
            const recreated = recreateElementForSemanticTagChange(id, type);
            if (recreated) {
                return;
            }
        }
        applyPropPatch(node, id, type, name);
        reloadParentVideoForSourceUpdate(id, name);
    };
    const resolveAttachedInsertionAnchor = (parentId, children, startIndex, parentContainer) => {
        for (let siblingIndex = startIndex; siblingIndex < children.length; siblingIndex += 1) {
            const siblingId = children[siblingIndex];
            if (siblingId === undefined) {
                continue;
            }
            const candidate = rangeManager.getFirstAttachedRenderedNode(siblingId, parentContainer);
            if (candidate) {
                return candidate;
            }
        }
        const terminal = rangeManager.getTerminalNodeForParent(parentId);
        return terminal?.parentNode === parentContainer ? terminal : null;
    };
    const handleInsertChild = (parentId, childId, index) => {
        const child = nodeMap.get(childId);
        const parentContainer = rangeManager.getContainerForParent(parentId);
        if (!child || !parentContainer) {
            return;
        }
        const children = childMap.get(parentId) ?? [];
        children.splice(index, 0, childId);
        childMap.set(parentId, children);
        parentMap.set(childId, parentId);
        const beforeNode = resolveAttachedInsertionAnchor(parentId, children, index + 1, parentContainer);
        rangeManager.insertRenderedRange(childId, parentContainer, beforeNode, document);
        reloadVideoForSourceChildChange(parentId, childId);
    };
    const handleRemoveChild = (parentId, childId) => {
        if (!nodeMap.has(parentId) || !nodeMap.has(childId)) {
            return;
        }
        rangeManager.detachRenderedRange(childId);
        const children = childMap.get(parentId);
        if (children) {
            const index = children.indexOf(childId);
            if (index >= 0) {
                children.splice(index, 1);
            }
        }
        parentMap.delete(childId);
        reloadVideoForSourceChildChange(parentId, childId);
    };
    const handleMoveChild = (parentId, childId, index) => {
        const parentContainer = rangeManager.getContainerForParent(parentId);
        if (!parentContainer || !nodeMap.has(childId)) {
            return;
        }
        const children = childMap.get(parentId) ?? [];
        const existingIndex = children.indexOf(childId);
        if (existingIndex >= 0) {
            children.splice(existingIndex, 1);
        }
        children.splice(index, 0, childId);
        childMap.set(parentId, children);
        parentMap.set(childId, parentId);
        const beforeNode = resolveAttachedInsertionAnchor(parentId, children, index + 1, parentContainer);
        rangeManager.insertRenderedRange(childId, parentContainer, beforeNode, document);
        reloadVideoForSourceChildChange(parentId, childId);
    };
    const handleSetText = (id, value) => {
        const node = nodeMap.get(id);
        if (!node) {
            return;
        }
        setNodeText(node, value);
    };
    const getNode = (id) => nodeMap.get(id) ?? null;
    const dispose = () => {
        cleanupAllRefBindings(refBindings);
        nodeMap.clear();
        nodeTypes.clear();
        eventHandlers.clear();
        childMap.clear();
        parentMap.clear();
        nodeProps.clear();
        nodeThemes.clear();
        nodeComposedSchemaTypes.clear();
        nodeDataKeys.clear();
        refBindings.clear();
        inputStates.clear();
        pendingVideoReloads.clear();
        rootId = null;
    };
    return { apply, dispose, getNode };
    function recreateElementForSemanticTagChange(nodeId, type) {
        if (!type || type === 'Fragment' || type === 'TextNode') {
            return null;
        }
        const current = nodeMap.get(nodeId);
        if (!current || current.nodeType !== ELEMENT_NODE) {
            return null;
        }
        const props = nodeProps.get(nodeId) ?? {};
        const expectedTag = mapTypeToTag(type, props);
        const currentElement = current;
        if (currentElement.tagName.toLowerCase() === expectedTag) {
            return null;
        }
        const recreated = createNode({ id: nodeId, type, props, key: undefined }, document);
        if (recreated.nodeType !== ELEMENT_NODE) {
            return null;
        }
        const recreatedElement = recreated;
        while (currentElement.firstChild) {
            recreatedElement.appendChild(currentElement.firstChild);
        }
        const parent = currentElement.parentNode;
        if (parent) {
            parent.replaceChild(recreatedElement, currentElement);
        }
        else if (rootId === nodeId) {
            mountContainer.replaceChildren(recreatedElement);
        }
        const handlers = eventHandlers.get(nodeId);
        if (handlers) {
            handlers.clear();
            eventHandlers.delete(nodeId);
        }
        cleanupRefBinding(nodeId, refBindings);
        nodeDataKeys.delete(nodeId);
        nodeMap.set(nodeId, recreatedElement);
        applyAllProps(recreatedElement, nodeId, type);
        return recreatedElement;
    }
    function applyAllProps(element, nodeId, type) {
        const props = nodeProps.get(nodeId) ?? {};
        const theme = nodeThemes.get(nodeId) ?? getTheme();
        const publicProps = stripInternalRuntimeProps(props);
        if (options.schemaValidation &&
            type &&
            type !== 'Fragment' &&
            type !== 'TextNode') {
            validateUINodeSchema({
                id: nodeId,
                type,
                props: publicProps,
                children: [],
                ...(nodeComposedSchemaTypes.get(nodeId)?.length
                    ? { _runtimeComposedSchemaTypes: nodeComposedSchemaTypes.get(nodeId) }
                    : {}),
            }, options.schemaValidation, theme);
        }
        applyClassNames(element, type, publicProps, theme);
        applyStyles(element, props);
        applyRefBinding(element, nodeId, props, refBindings);
        applyAttributes(element, nodeId, props, attributeContext);
        applyEvents(element, nodeId, props, eventHandlers, inputStates);
    }
    function applyPropPatch(element, nodeId, type, propName) {
        const props = nodeProps.get(nodeId) ?? {};
        const theme = nodeThemes.get(nodeId) ?? getTheme();
        const publicProps = stripInternalRuntimeProps(props);
        if (options.schemaValidation &&
            type &&
            type !== 'Fragment' &&
            type !== 'TextNode') {
            validateUINodeSchema({
                id: nodeId,
                type,
                props: publicProps,
                children: [],
                ...(nodeComposedSchemaTypes.get(nodeId)?.length
                    ? { _runtimeComposedSchemaTypes: nodeComposedSchemaTypes.get(nodeId) }
                    : {}),
            }, options.schemaValidation, theme);
        }
        if (propName === THEME_SCOPE_PROP || isClassProp(propName) || isManagedClassProp(propName)) {
            applyClassNames(element, type, publicProps, theme);
            if (propName === 'width' || propName === 'height') {
                applyAttributes(element, nodeId, props, attributeContext);
            }
            return;
        }
        if (isStyleProp(propName)) {
            applyStyles(element, props);
            // Keep SVG width/height attributes in sync when size props change.
            if (propName === 'width' || propName === 'height') {
                applyAttributes(element, nodeId, props, attributeContext);
            }
            return;
        }
        if (isRefProp(propName)) {
            applyRefBinding(element, nodeId, props, refBindings);
            return;
        }
        if (isEventProp(propName)) {
            applyEventProp(element, nodeId, props, propName, eventHandlers, inputStates);
            return;
        }
        // Default to attribute update for all other props.
        applyAttributes(element, nodeId, props, attributeContext);
    }
    function reloadParentVideoForSourceUpdate(sourceId, propName) {
        if (propName !== 'src' && propName !== 'type') {
            return;
        }
        const parentId = parentMap.get(sourceId);
        if (!parentId) {
            return;
        }
        reloadVideoForSourceChildChange(parentId, sourceId);
    }
    function reloadVideoForSourceChildChange(parentId, childId) {
        const videoId = findNearestElementTag(parentId, 'VIDEO');
        if (!videoId || !subtreeContainsElementTag(childId, 'SOURCE')) {
            return;
        }
        pendingVideoReloads.add(videoId);
    }
    function flushPendingVideoReloads() {
        const videoIds = [...pendingVideoReloads];
        pendingVideoReloads.clear();
        for (const videoId of videoIds) {
            const video = nodeMap.get(videoId);
            if (video?.nodeType === ELEMENT_NODE && video.tagName === 'VIDEO') {
                video.load();
            }
        }
    }
    function subtreeContainsElementTag(nodeId, tagName) {
        if (isElementTag(nodeId, tagName)) {
            return true;
        }
        return (childMap.get(nodeId) ?? [])
            .some((childId) => subtreeContainsElementTag(childId, tagName));
    }
    function findNearestElementTag(nodeId, tagName) {
        let currentId = nodeId;
        while (currentId) {
            if (isElementTag(currentId, tagName)) {
                return currentId;
            }
            currentId = parentMap.get(currentId);
        }
        return undefined;
    }
    function isElementTag(nodeId, tagName) {
        const node = nodeMap.get(nodeId);
        return node?.nodeType === ELEMENT_NODE && node.tagName === tagName;
    }
    function setNodeText(node, value) {
        if (node.nodeType === TEXT_NODE) {
            node.nodeValue = value;
            return;
        }
        if (node.nodeType === ELEMENT_NODE) {
            node.textContent = value;
        }
    }
}
//# sourceMappingURL=patch-dom.js.map